// QZ Tray request signing — makes QZ trust the register so it STOPS the "untrusted website" Allow
// prompt. ONE fleet certificate/key pair is provided via env (generated once by Quickvee, NOT per
// merchant); every lane uses it. The private key only grants "talk to QZ Tray on the local PC"
// (print / open drawer), so shipping it in the client bundle is low-risk and standard for QZ-based
// POS. Signing runs in-browser via Web Crypto (needs a secure context: HTTPS or localhost — the
// register is HTTPS).
//
// Env (CRA: must be REACT_APP_*). Use literal \n for PEM newlines in .env:
//   REACT_APP_QZ_CERT=
//   REACT_APP_QZ_PRIVATE_KEY=
//
// PER-LANE (one-time, done by the installer — NOT the merchant): put the matching public cert at
//   C:\Program Files\QZ Tray\override.crt   (see docs/qz-tray/override.crt) then restart QZ Tray.
// On the machine where the cert was generated via QZ Site Manager, QZ already trusts it (auto-installed).
// Until override.crt is in place on a lane, QZ still prompts (signing is present, cert not yet trusted).

function normalizePem(value) {
  if (!value) return "";
  return String(value).replace(/\\n/g, "\n").trim();
}

const QZ_CERT = normalizePem(process.env.REACT_APP_QZ_CERT);
const QZ_PRIVATE_KEY = normalizePem(process.env.REACT_APP_QZ_PRIVATE_KEY);

function pemToDer(pem) {
  const b64 = pem
    .replace(/-----BEGIN [^-]+-----/, "")
    .replace(/-----END [^-]+-----/, "")
    .replace(/\s+/g, "");
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}

let keyPromise = null;
function signingKey() {
  if (!keyPromise) {
    keyPromise = crypto.subtle.importKey(
      "pkcs8",
      pemToDer(QZ_PRIVATE_KEY),
      { name: "RSASSA-PKCS1-v1_5", hash: "SHA-512" },
      false,
      ["sign"]
    );
  }
  return keyPromise;
}

// Sign QZ's challenge with the fleet private key → base64 (RSA-SHA512, matching setSignatureAlgorithm).
export async function signRequest(toSign) {
  const key = await signingKey();
  const sig = await crypto.subtle.sign("RSASSA-PKCS1-v1_5", key, new TextEncoder().encode(String(toSign)));
  const bytes = new Uint8Array(sig);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

export function signingAvailable() {
  return (
    QZ_CERT.indexOf("BEGIN CERTIFICATE") >= 0 &&
    QZ_PRIVATE_KEY.indexOf("PRIVATE KEY") >= 0 &&
    typeof crypto !== "undefined" &&
    !!crypto.subtle
  );
}

// Wire QZ's security promises. When the fleet cert/key are present and Web Crypto is available, sign
// every request (silent once override.crt is trusted). Otherwise fall back to the unsigned/anonymous
// flow (QZ prompts, but the drawer/print still works) so nothing ever breaks.
export function configureSigning(qz) {
  if (!signingAvailable()) {
    qz.security.setCertificatePromise((resolve) => resolve());
    qz.security.setSignaturePromise(() => (resolve) => resolve());
    return false;
  }
  qz.security.setCertificatePromise((resolve) => resolve(QZ_CERT));
  if (qz.security.setSignatureAlgorithm) qz.security.setSignatureAlgorithm("SHA512");
  qz.security.setSignaturePromise((toSign) => (resolve, reject) => {
    signRequest(toSign).then(resolve).catch(reject);
  });
  return true;
}
